import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import BrutalHeader from './BrutalHeader';
import './Signup.css';

const INDUSTRIES = [
  'Law',
  'Publishing',
  'Healthcare',
  'Real Estate',
  'Service',
  'Restaurants',
];

function Signup({ sendEmail = true }: { sendEmail?: boolean } = {}) {
  const [searchParams] = useSearchParams();

  const [businessName, setBusinessName] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [otherIndustry, setOtherIndustry] = useState('');
  const [revenue, setRevenue] = useState('');
  const [location, setLocation] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const referredBy = searchParams.get('ref') || '';

  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const industry = selectedIndustry === 'Other' ? otherIndustry : selectedIndustry;

  const validate = () => {
    const e: Record<string, string> = {};
    if (!businessName.trim()) e.businessName = 'Required';
    if (!selectedIndustry) e.industry = 'Please select an industry';
    if (selectedIndustry === 'Other' && !otherIndustry.trim()) e.otherIndustry = 'Required';
    if (!revenue) e.revenue = 'Please select a range';
    if (!firstName.trim()) e.firstName = 'Required';
    if (!lastName.trim()) e.lastName = 'Required';
    if (!email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Invalid email';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          businessName,
          industry,
          location: location || null,
          revenue,
          referredBy: referredBy || null,
          sendEmail,
        }),
      });

      if (!res.ok) throw new Error();
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="signup-page">
        <BrutalHeader minimal />
        <div className="signup-center">
          <div className="signup-card">
            <div className="signup-success-icon">✓</div>
            <h1 className="signup-success-heading">You're on the list.</h1>
            <p className="signup-success-body">
              We'll review your information and reach out shortly to discuss next steps.
            </p>
            {referredBy || email ? (() => {
              const referralUrl = `${window.location.origin}/signup?ref=${encodeURIComponent(email)}`;
              return (
                <div className="signup-referral-card">
                  <p className="signup-referral-text">Know someone else with valuable data?</p>
                  <p className="signup-referral-earn">You'll earn <strong>10%</strong> of any profits from the licensing of their data.</p>
                  <label className="signup-referral-label">Copy your referral link</label>
                  <div className="signup-referral-copy">
                    <span className="signup-referral-url">{referralUrl}</span>
                    <button
                      className="signup-referral-copy-btn"
                      type="button"
                      onClick={() => { navigator.clipboard.writeText(referralUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              );
            })() : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <BrutalHeader minimal />
      <div className="signup-center">
        <div className="signup-card">
          <div className="signup-card-header">
            <span className="signup-eyebrow">Partner Application</span>
            <h1 className="signup-heading">Let's build something together.</h1>
            <p className="signup-subheading">
              We'll assess your data, help you monetize it, and work with you to apply AI
              across your operations. Tell us about your business to get started.
            </p>
          </div>

          <div className="signup-fields">
            {/* First Name */}
            <div className="signup-field">
              <label className="signup-label">First Name</label>
              <input
                className={`signup-input${errors.firstName ? ' signup-input-error' : ''}`}
                type="text"
                placeholder="Jane"
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); setErrors((prev) => ({ ...prev, firstName: '' })); }}
              />
              {errors.firstName && <span className="signup-error">{errors.firstName}</span>}
            </div>

            {/* Last Name */}
            <div className="signup-field">
              <label className="signup-label">Last Name</label>
              <input
                className={`signup-input${errors.lastName ? ' signup-input-error' : ''}`}
                type="text"
                placeholder="Smith"
                value={lastName}
                onChange={(e) => { setLastName(e.target.value); setErrors((prev) => ({ ...prev, lastName: '' })); }}
              />
              {errors.lastName && <span className="signup-error">{errors.lastName}</span>}
            </div>

            {/* Email */}
            <div className="signup-field">
              <label className="signup-label">Email Address</label>
              <input
                className={`signup-input${errors.email ? ' signup-input-error' : ''}`}
                type="email"
                placeholder="jane@acmecorp.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors((prev) => ({ ...prev, email: '' })); }}
              />
              {errors.email && <span className="signup-error">{errors.email}</span>}
            </div>

            <div className="signup-divider" />

            {/* Business Name */}
            <div className="signup-field">
              <label className="signup-label">Business Name</label>
              <input
                className={`signup-input${errors.businessName ? ' signup-input-error' : ''}`}
                type="text"
                placeholder="Acme Corp"
                value={businessName}
                onChange={(e) => { setBusinessName(e.target.value); setErrors((prev) => ({ ...prev, businessName: '' })); }}
              />
              {errors.businessName && <span className="signup-error">{errors.businessName}</span>}
            </div>

            {/* Location */}
            <div className="signup-field">
              <label className="signup-label">Location</label>
              <input
                className={`signup-input${errors.location ? ' signup-input-error' : ''}`}
                type="text"
                placeholder="New York, NY"
                value={location}
                onChange={(e) => { setLocation(e.target.value); setErrors((prev) => ({ ...prev, location: '' })); }}
              />
              {errors.location && <span className="signup-error">{errors.location}</span>}
            </div>

            {/* Industry */}
            <div className="signup-field">
              <label className="signup-label">Industry</label>
              <div className="signup-industry-grid">
                {INDUSTRIES.map((ind) => (
                  <button
                    key={ind}
                    type="button"
                    className={`signup-industry-btn${selectedIndustry === ind ? ' signup-industry-btn-active' : ''}`}
                    onClick={() => { setSelectedIndustry(ind); setErrors((prev) => ({ ...prev, industry: '', otherIndustry: '' })); }}
                  >
                    {ind}
                  </button>
                ))}
                <button
                  type="button"
                  className={`signup-industry-btn${selectedIndustry === 'Other' ? ' signup-industry-btn-active' : ''}`}
                  onClick={() => { setSelectedIndustry('Other'); setErrors((prev) => ({ ...prev, industry: '' })); }}
                >
                  Other
                </button>
              </div>
              {errors.industry && <span className="signup-error">{errors.industry}</span>}
              {selectedIndustry === 'Other' && (
                <input
                  className={`signup-input signup-other-input${errors.otherIndustry ? ' signup-input-error' : ''}`}
                  type="text"
                  placeholder="Enter your industry"
                  value={otherIndustry}
                  onChange={(e) => { setOtherIndustry(e.target.value); setErrors((prev) => ({ ...prev, otherIndustry: '' })); }}
                  autoFocus
                />
              )}
              {errors.otherIndustry && <span className="signup-error">{errors.otherIndustry}</span>}
            </div>

            {/* Revenue */}
            <div className="signup-field">
              <label className="signup-label">Annual Revenue</label>
              <div className="signup-select-wrap">
                <select
                  className={`signup-input signup-select${errors.revenue ? ' signup-input-error' : ''}`}
                  value={revenue}
                  onChange={(e) => { setRevenue(e.target.value); setErrors((prev) => ({ ...prev, revenue: '' })); }}
                >
                  <option value="" disabled>Select a range</option>
                  <option value="Under $100K">Under $100K</option>
                  <option value="$100K–$1M">$100K–$1M</option>
                  <option value="$1M–$10M">$1M–$10M</option>
                  <option value="$10M–$50M">$10M–$50M</option>
                  <option value="$50M+">$50M+</option>
                </select>
                <span className="signup-select-arrow">
                  <svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1l5 5 5-5" stroke="rgba(255,255,255,0.45)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </div>
              {errors.revenue && <span className="signup-error">{errors.revenue}</span>}
            </div>

            {error && <p className="signup-submit-error">{error}</p>}

            <button
              className="signup-submit-btn"
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Submit Application →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
